/**
 * Iter-59 — Help Center primitives.
 *
 * Small, reusable building blocks for rendering help articles. Every
 * article's `sections[]` maps 1:1 to one of these components via
 * `HelpArticleRenderer`.
 */
import React from "react";
import {
  Warning, Info, CheckCircle, XCircle,
} from "@phosphor-icons/react";

export function HelpRoleBadge({ role }) {
  const label = {
    admin: "Admin",
    employee: "Employee",
    media_buyer: "Media Buyer",
    vendor: "Affiliate",
    buyer: "Buyer",
    shared: "Everyone",
  }[role] || role;
  const tone = {
    admin: "bg-black text-white",
    employee: "bg-zinc-800 text-white",
    media_buyer: "bg-indigo-700 text-white",
    vendor: "bg-emerald-700 text-white",
    buyer: "bg-amber-600 text-white",
    shared: "bg-zinc-200 text-zinc-800",
  }[role] || "bg-zinc-200 text-zinc-800";
  return (
    <span
      data-testid={`help-role-badge-${role}`}
      className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${tone}`}
    >
      {label}
    </span>
  );
}

// Renders **bold**, *italic* and `code` inside guide text.
export function Rich({ text }) {
  if (typeof text !== "string") return text ?? null;
  return text.split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g).map((part, i) => {
    if (part.length > 2 && part.startsWith("`") && part.endsWith("`")) {
      return <code key={i} className="font-mono text-[0.85em] bg-zinc-100 border border-zinc-200 px-1 break-all">{part.slice(1, -1)}</code>;
    }
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (/^\*[^*].*\*$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export function HelpCallout({ variant = "info", children }) {
  const map = {
    info:    { icon: Info,        bg: "bg-blue-50",   border: "border-blue-700",   iconColor: "text-blue-700" },
    warn:    { icon: Warning,     bg: "bg-amber-50",  border: "border-amber-700",  iconColor: "text-amber-700" },
    danger:  { icon: XCircle,     bg: "bg-rose-50",   border: "border-rose-700",   iconColor: "text-rose-700" },
    success: { icon: CheckCircle, bg: "bg-emerald-50", border: "border-emerald-700", iconColor: "text-emerald-700" },
  };
  const cfg = map[variant] || map.info;
  const Icon = cfg.icon;
  return (
    <div
      data-testid={`help-callout-${variant}`}
      className={`border-l-4 ${cfg.border} ${cfg.bg} p-3 my-4 flex gap-3`}
    >
      <Icon size={20} weight="bold" className={cfg.iconColor + " flex-shrink-0 mt-0.5"} />
      <div className="text-sm text-zinc-800"><Rich text={children} /></div>
    </div>
  );
}

export function HelpSteps({ items }) {
  return (
    <ol data-testid="help-steps" className="my-4 space-y-2 list-none pl-0">
      {items.map((item, i) => (
        <li key={i} className="flex gap-3">
          <span className="flex-shrink-0 w-6 h-6 bg-black text-white text-xs font-bold flex items-center justify-center">
            {i + 1}
          </span>
          <span className="text-sm text-zinc-800 leading-relaxed pt-0.5"><Rich text={item} /></span>
        </li>
      ))}
    </ol>
  );
}

export function HelpBullets({ items }) {
  return (
    <ul data-testid="help-bullets" className="my-3 list-disc list-outside pl-5 space-y-1">
      {items.map((it, i) => (
        <li key={i} className="text-sm text-zinc-800 leading-relaxed"><Rich text={it} /></li>
      ))}
    </ul>
  );
}

export function HelpScreenshot({ asset, caption, annotations, takenAt, outdated }) {
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/help/assets/${asset}${takenAt ? `?v=${encodeURIComponent(takenAt)}` : ""}`;
  const [dims, setDims] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const points = (annotations || []).filter(
    (a) => a && (a.type ?? "point") === "point" && typeof a.x === "number" && typeof a.y === "number",
  );
  const boxes = (annotations || []).filter(
    (a) => a && a.type === "box" && typeof a.x === "number" && typeof a.y === "number",
  );
  const captionAnnotations = (annotations || []).filter((a) => a && a.num && a.label);
  return (
    <figure data-testid="help-screenshot" className="my-4 border border-black">
      <div className="relative select-none" data-testid="help-screenshot-canvas">
        <a href={src} target="_blank" rel="noopener noreferrer" title="Open full size" data-testid="help-screenshot-open">
          <img
            src={src}
            alt={caption || asset}
            loading="lazy"
            className="w-full block"
            onLoad={(e) => setDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            onError={(e) => {
              e.target.style.opacity = "0.35";
              e.target.title = "Screenshot unavailable";
              setFailed(true);
            }}
          />
        </a>
        {/* Numbered circular markers — coordinates are % of image container. */}
        {points.map((a, i) => (
          <span
            key={`p-${i}`}
            data-testid={`help-annot-point-${a.num ?? i + 1}`}
            style={{ left: `${a.x}%`, top: `${a.y}%` }}
            className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          >
            <span className="relative flex items-center justify-center">
              <span className="absolute inline-flex h-9 w-9 rounded-full bg-yellow-300 opacity-70 animate-ping" />
              <span className="relative inline-flex h-7 w-7 rounded-full bg-black text-white text-xs font-bold items-center justify-center ring-2 ring-yellow-300 shadow-[2px_2px_0_0_#000]">
                {a.num ?? i + 1}
              </span>
            </span>
          </span>
        ))}
        {/* Highlight rectangles. */}
        {boxes.map((a, i) => (
          <span
            key={`b-${i}`}
            data-testid={`help-annot-box-${a.num ?? i + 1}`}
            style={{
              left: `${a.x}%`,
              top: `${a.y}%`,
              width: `${a.width || 10}%`,
              height: `${a.height || 5}%`,
            }}
            className="absolute border-4 border-yellow-400 bg-yellow-300/20 pointer-events-none"
            title={a.label}
          />
        ))}
        {failed && (
          <div
            data-testid="help-screenshot-fallback"
            className="absolute inset-0 flex items-center justify-center bg-white/50 pointer-events-none"
          >
            <div className="border border-black bg-yellow-50 px-3 py-1 text-[10px] font-mono uppercase tracking-widest">
              screenshot unavailable
            </div>
          </div>
        )}
      </div>
      {(caption || captionAnnotations.length > 0 || takenAt || outdated) && (
        <figcaption className="border-t border-black bg-zinc-50 px-3 py-2 text-xs font-mono text-zinc-700">
          {captionAnnotations.length > 0 && (
            <div className="mb-1 text-black">
              {captionAnnotations
                .sort((a, b) => (a.num ?? 0) - (b.num ?? 0))
                .map((a) => (
                  <span key={a.num} className="mr-3">
                    <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-black text-white text-[9px] font-bold mr-1">
                      {a.num}
                    </span>
                    {a.label}
                  </span>
                ))}
            </div>
          )}
          {caption}
          {takenAt && (
            <div className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500" data-testid="help-screenshot-date">
              Screenshot taken {new Date(takenAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
          {outdated && (
            <div className="mt-1 border border-amber-600 bg-amber-50 px-2 py-1 text-[11px] text-amber-900" data-testid="help-screenshot-outdated">
              This picture may be outdated — the screen changed after it was taken. Retake it from the Guide home.
            </div>
          )}
        </figcaption>
      )}
    </figure>
  );
}

export function HelpKV({ pairs }) {
  return (
    <dl data-testid="help-kv" className="my-3 border border-black">
      {pairs.map(([k, v], i) => (
        <div key={i} className={"grid grid-cols-3 " + (i > 0 ? "border-t border-black" : "")}>
          <dt className="col-span-1 bg-zinc-100 px-3 py-2 font-mono text-xs uppercase tracking-widest border-r border-black">{k}</dt>
          <dd className="col-span-2 px-3 py-2 text-sm text-zinc-800">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function HelpTable({ headers, rows }) {
  return (
    <div className="overflow-x-auto my-3">
      <table data-testid="help-table" className="w-full text-sm border-collapse">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="border border-black bg-black text-white uppercase tracking-widest text-[10px] px-2 py-1 text-left">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} className="border border-zinc-300 px-2 py-1 align-top">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function HelpCode({ language, code }) {
  return (
    <pre data-testid="help-code" className="bg-black text-emerald-300 p-3 my-3 overflow-x-auto font-mono text-xs">
      <code>{code}</code>
    </pre>
  );
}

export function HelpFAQ({ items }) {
  return (
    <div data-testid="help-faq" className="my-4 space-y-3">
      {items.map((it, i) => (
        <details key={i} className="border border-black">
          <summary className="cursor-pointer bg-zinc-100 px-3 py-2 font-bold text-sm border-b border-black">{it.q}</summary>
          <div className="px-3 py-2 text-sm text-zinc-800"><Rich text={it.a} /></div>
        </details>
      ))}
    </div>
  );
}
